import { Input, Table, Tag } from "antd";
import searchIcon from "@assets/icons/search.svg";
import plusIcon from "@assets/icons/plus.svg";
import plusActiveIcon from "@assets/icons/plus_active.svg";
import { IconButton, PrimaryButton, SecondaryButton } from "@common/Buttons";
import { useMemo, useState } from "react";
import { debounce } from "lodash";
import {
  useCreateUserMutation,
  useEditUserMutation,
  useFetchUsersQuery,
} from "@store/master";
import { LoadingOutlined } from "@ant-design/icons";
import editIcon from "@assets/icons/edit.svg";
import editActiveIcon from "@assets/icons/edit_active.svg";
import UserForm from "../components/UserForm";
import DrawerForm from "../components/DrawerForm";
import { useNavigate } from "react-router-dom";
import { DEFAULT_PAGINATION } from "@constant/pagination";

export default function Users() {
  const [search, setSearch] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [resetTrigger, setResetTrigger] = useState(0);

  const {
    data: users,
    isLoading,
    isFetching,
  } = useFetchUsersQuery({
    search,
    page: pagination.current,
    perPage: pagination.pageSize,
  });
  const [createUser, { isLoading: isCreateUserLoading }] =
    useCreateUserMutation();
  const [editUser, { isLoading: isEditUserLoading }] = useEditUserMutation();
  const navigate = useNavigate();

  const [editItem, setEditItem] = useState(null);

  const openForm = () => setIsFormOpen(true);
  const closeForm = () => {
    setIsFormOpen(false);
    setResetTrigger((prev) => prev + 1);
  };

  // Debounce the search
  const debouncedOnSearch = useMemo(
    () =>
      debounce((value) => {
        setSearch(value);
      }, 300),
    []
  );

  // Handle page change
  const handleTablePaginationChange = (newPagination) => {
    setPagination({
      ...pagination,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  const handleCreateUser = async (data, isEdit) => {
    if (isEdit) {
      const res = await editUser(data);
      if (res?.data?.success) {
        closeForm();
        setResetTrigger((prev) => prev + 1);
      }
    } else {
      const res = await createUser(data);
      if (res?.data?.success) {
        closeForm();
        setResetTrigger((prev) => prev + 1);
      }
    }
  };

  //columns
  const columns = [
    {
      title: () => "Full Name",
      dataIndex: "full_name",
      key: "full_name",
    },
    {
      title: () => "Email ID",
      dataIndex: "email_id",
      key: "email_id",
    },
    {
      title: () => "Role",
      dataIndex: "role",
      key: "role",
    },
    {
      title: () => "Status",
      dataIndex: "status",
      key: "status",
      render: (isActive) => {
        return isActive ? (
          <div className="px-3 font-medium text-[10px] rounded-full py-1.5 bg-[#D0FFF1] text-primary-tint w-min">
            Active
          </div>
        ) : (
          <div className="px-3 font-medium text-[10px] rounded-full py-1.5 bg-red-100 text-red-500 w-min whitespace-nowrap  ">
            In-active
          </div>
        );
      },
    },
    {
      title: () => "Actions",
      dataIndex: "status",
      key: "status",
      render: (_, record) => {
        return (
          <IconButton
            icon={editIcon}
            activeIcon={editActiveIcon}
            onClick={() => {
              setIsFormOpen(record.key);
              setEditItem(record);
            }}
          />
        );
      },
    },
  ];

  //transformed users
  const transformedUsers = users?.data.map((user) => {
    return {
      key: user.id,
      email_id: user.email,
      full_name: `${user.firstName} ${user.lastName}`,
      role: user.isAdmin ? "Admin" : "User",
      status: user.isActive,
      user,
    };
  });

  return (
    <div className="h-full p-2 overflow-x-hidden overflow-y-auto rounded-lg md:p-5 animate-fadeIn bg-secondary-main custom-scrollbar">
      <div className="flex flex-col gap-5">
        {/* heading */}
        <div className="flex flex-col pb-1.5 items-start justify-between border-b md:items-center md:flex-row border-b-border">
          <label className="text-lg font-semibold leading-6 text-text-header">
            Manage Users
          </label>
          {/* actions */}
          <div className="flex gap-2">
            <Input
              prefix={<img src={searchIcon} alt="search icon" />}
              placeholder="Search by name"
              className="!w-[300px] !h-8 !text-sm !rounded-lg !border !border-[#D0D5DD]"
              onChange={(e) => debouncedOnSearch(e.target.value)}
            />
            <PrimaryButton
              onClick={openForm}
              icon={plusIcon}
              activeIcon={plusActiveIcon}
            >
              Add User
            </PrimaryButton>
            <SecondaryButton onClick={() => navigate("/settings")}>
              Go Back
            </SecondaryButton>
          </div>
        </div>

        {/* table */}
        <div className={`w-full animate-fadeIn`}>
          <Table
            columns={columns}
            dataSource={transformedUsers}
            loading={{
              spinning: isLoading || isFetching,
              indicator: (
                <LoadingOutlined
                  spin
                  className="!text-primary-main text-center"
                />
              ),
            }}
            pagination={{
              ...pagination,
              total: users?.pagination?.total_records || 0,
            }}
            onChange={handleTablePaginationChange}
            className=" !rounded-lg"
            components={{
              body: {
                cell: (props) => {
                  return <td {...props} className="!py-4 !px-6 !text-xs" />;
                },
              },
              header: {
                cell: (props) => {
                  return (
                    <th
                      {...props}
                      className="!py-4 !px-6 bg-[#EAECF0] !text-xs !font-medium"
                    >
                      {props.children}
                    </th>
                  );
                },
              },
            }}
          />
        </div>

        {/* form */}
        <DrawerForm
          label="Create User"
          isOpen={isFormOpen}
          onClose={closeForm}
          content={
            <UserForm
              onSave={handleCreateUser}
              onCancel={closeForm}
              isEdit={typeof isFormOpen === "number"}
              details={editItem}
              loading={isCreateUserLoading || isEditUserLoading}
              resetTrigger={resetTrigger}
            />
          }
        />
      </div>
    </div>
  );
}
